import {
    useEffect,
    useRef,
    useState,
    VoidFunctionComponent,
    useMemo,
    MutableRefObject,
    useCallback,
    useLayoutEffect,
    ComponentProps,
} from 'react';
import { ThreeEvent, useFrame } from '@react-three/fiber';
import { BigNumber, ethers } from 'ethers';
import { useAccount, usePrepareContractWrite, useContractWrite, useProvider } from 'wagmi';
import { Image, Environment, Plane, Text, useTexture } from '@react-three/drei';
import { DirectionalLight, Group, MathUtils, MeshBasicMaterial, Object3D } from 'three';
import { OnlyBot, OnlyBotAnchor } from '@animavirtuality/onlybots-core';
import OnlyBotsToken from '@/contracts/OnlyBotsToken.json';
import OnlyBotsEscrow from '@/contracts/OnlyBotsEscrow.json';
import { BotInstancedMesh } from '@/lib/types';
import { Screen } from '@/components/Screen';
import { useResponsive } from '@/hooks/useResponsive';
import { garamondNarrowLight, helveticaNeue, monaco } from '@/lib/fonts';
import { PlaystationButton } from '@/components/PlaystationButton';
import { eggData } from '@/lib/eggData';
import { formatVoxelMaterialsFromBot } from '@/lib/voxelBot';
import { applyMaterial, DEFAULT_COLOR } from '@/lib/materials';
import { useLerpedMouse } from '@/hooks/useLerpedMouse';
import { useVoxelExplosion } from '@/hooks/useVoxelExplosion';
import { BUY_URL, escrowContractAddress, MAX_VOXEL_COUNT, tokenContractAddress } from '@/lib/globals';
import { getTokenIdFromTransferLogs } from '@/lib/parseLogs';
import recaptchaImg1 from '@/img/recaptcha-image-1.png';
import xi from '@/img/xi.png';
import { SoldOut } from '@/components/scenes/SoldOut';
import { clampedLerp } from '@/lib/mathUtils';
import { convertShaderToPreset } from '@/lib/presets';
import { useDeviceOrientation } from '@/hooks/useDeviceOrientation';
import { SelectBloom } from '@/components/SelectBloom';
import { useWalletContext } from '@/context/WalletContext';
import { useRouterContext } from '@/context/RouterContext';
import { bgBlack } from '@/lib/colors';
import mintingSound from '@/sounds/ob-music-minting.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import switchBot1Sound from '@/sounds/ob-sfx-title-switch-bot-1.mp3';

const TRANSFORMATION_STORAGE = new Object3D();
const EXPLODE_TIME = 0.6;
const SWIRL_TIME = 0.5;
const CONSOLIDATE_TIME = 1.0;
const NAME_FADE_IN_TIME = 1.5;
const VOXEL_CLOUD_SPEED = 0.009;
const VOXEL_CLOUD_SIZE = 0.6;
const TIME_TO_CENTER_EGG = 1;
const EGG_SCALE = 0.15;
const BOT_SCALE = 0.3;
const LIGHT_INTENSITY = 2;
const REDIRECT_TO_EDITION_DURATION = 7;

type TextProps = ComponentProps<typeof Text>;
const scaleAnchor = (anchor: OnlyBotAnchor): OnlyBotAnchor => ({
    x: BOT_SCALE * anchor.x,
    y: BOT_SCALE * anchor.y,
    z: BOT_SCALE * anchor.z,
});

const disclaimerTextProps = {
    font: monaco,
    fontSize: 0.08,
    color: 'white',
    maxWidth: 3,
};

const errorMapping = {
    InvalidMintValue: "Transaction value doesn't match mint price.",
    InvalidTokenId: 'Sold out of current batch.',
    AddressAlreadyMinted: 'Only one bot allowed per batch.',
    InvalidSignature: "Authorized wallet doesn't match current wallet.",
    AddressNotAuthorized: 'Wallet not authorized to mint',
    InvalidMintAddress: 'Invalid mint address.',
    InvalidBurnAddress: 'Invalid burn address.',
    InvalidRNGMax: "Couldn't assign your bot.",
    InvalidRNGRoll: "Couldn't assign your bot.",
    InvalidBotId: "Couldn't assign your bot.",
    BrokenBotAssignment: "Couldn't assign your bot.",
    MissingBotAssignment: "Couldn't assign your bot.",
    NoDataContracts: 'Sold out of current batch.',
    WrongValue: "Transaction value doesn't match mint price.",
    AlreadyClaimed: 'Only one bot allowed per address.',
    AlreadyClaimedMax: 'You have hit the claim limit.',
    NoTokensRemaining: 'Sold out of current batch.',
};

interface ContractError {
    code?: number;
    data?: {
        code: number;
        originalError: {
            message: string;
            data: string;
        };
    };
    message: string;
}

type EthersError = {
    error: ContractError;
} & Error;

interface Props {
    cancel: () => void;
    batchPrice: BigNumber;
    remainingInBatch: number;
}

export const Mint: VoidFunctionComponent<Props> = ({ cancel, batchPrice, remainingInBatch }) => {
    const { signature } = useWalletContext();
    const [hovered, setHovered] = useState<boolean>(false);
    const { navigate } = useRouterContext();
    const [txSubmitted, setTxSubmitted] = useState<boolean>(false);
    const provider = useProvider();
    const tokenContract = useMemo(
        () => new ethers.Contract(tokenContractAddress, OnlyBotsToken.abi, provider),
        [provider]
    );
    const escrowContract = useMemo(
        () => new ethers.Contract(escrowContractAddress, OnlyBotsEscrow.abi, provider),
        [provider]
    );
    const [mintErrorCode, setMintErrorCode] = useState<number>();
    const [mintErrorMessage, setMintErrorMessage] = useState<string>();
    const [mintErrorAllowRetry, setMintErrorAllowRetry] = useState<boolean>(false);
    const light1 = useRef<DirectionalLight>(new DirectionalLight());
    const light2 = useRef<DirectionalLight>(new DirectionalLight());
    const { address } = useAccount();
    const { scale, responsiveValue } = useResponsive();
    const [mintedBot, setMintedBot] = useState<OnlyBot | null>(null);
    const instance0 = useRef<BotInstancedMesh>(null);
    const instance1 = useRef<BotInstancedMesh>(null);
    const instance2 = useRef<BotInstancedMesh>(null);
    const instance3 = useRef<BotInstancedMesh>(null);
    const instancedMeshRefs = useMemo(() => [instance0, instance1, instance2, instance3], []);
    const { orientation } = useDeviceOrientation();
    const [playMintingSound, { stop: stopMintingSound }] = useOnlybotsSound(mintingSound);
    const [playMintedSound] = useOnlybotsSound(switchBot1Sound);

    const currentBot = mintedBot ?? eggData;
    const materials = useMemo(() => formatVoxelMaterialsFromBot(currentBot), [currentBot]);
    const mouse = useLerpedMouse();
    const groupRef = useRef<Group>(new Group());
    const nameRef = useRef<TextProps>();
    const { explode, consolidate } = useVoxelExplosion(
        instancedMeshRefs,
        materials,
        groupRef,
        EXPLODE_TIME,
        SWIRL_TIME,
        CONSOLIDATE_TIME,
        VOXEL_CLOUD_SPEED,
        VOXEL_CLOUD_SIZE
    );
    const eggPositionTimer = useRef<number>(0);
    const timeSinceBotMinted = useRef<number>(0);
    const backgroundMaterialRef = useRef() as MutableRefObject<MeshBasicMaterial>;
    const xiCharacterRef = useRef<MeshBasicMaterial>(new MeshBasicMaterial());
    const xiCharacter = useTexture(xi);
    const walletAddress = address ? `${address.substring(0, 8)}...${address.substring(address.length - 2)}` : 'anon';
    const leadTextProps = {
        textAlign: 'center' as const,
        font: garamondNarrowLight,
        fontSize: responsiveValue(0.36, 0.28),
        color: 'white',
        maxWidth: responsiveValue(5.5, 2.9),
        lineHeight: 1.1,
    };

    const {
        config,
        isError: isPrepareError,
        error: prepareError,
        refetch,
    } = usePrepareContractWrite({
        address: escrowContractAddress,
        abi: OnlyBotsEscrow.abi,
        functionName: 'claim',
        args: signature ?? undefined,
        overrides: {
            value: batchPrice,
        },
    });
    const gasBufferRequest = useMemo(
        () =>
            config.request === undefined
                ? undefined
                : {
                      ...config.request,
                      gasLimit: config.request.gasLimit.add('20000'),
                  },
        [config]
    );

    const redirectToEdition = useCallback(
        (tokenId: number) => {
            setTimeout(() => navigate(`/${tokenId}`), REDIRECT_TO_EDITION_DURATION * 1000);
        },
        [navigate]
    );

    useEffect(() => {
        if (txSubmitted) {
            playMintingSound();
        }
    }, [playMintingSound, txSubmitted]);

    useEffect(() => {
        if (mintedBot) {
            playMintedSound();
            stopMintingSound();
        }
    }, [mintedBot, playMintedSound, stopMintingSound]);

    const onSuccess = useCallback(
        async (data) => {
            try {
                setTxSubmitted(true);
                setMintErrorMessage(undefined);
                setMintErrorCode(undefined);
                explode();
                const receipt = await data?.wait();
                if (receipt) {
                    const tokenId = await getTokenIdFromTransferLogs(receipt.logs, tokenContract);
                    if (tokenId) {
                        const mintedBot = await tokenContract.getBotData(tokenId.toNumber());
                        setMintedBot(OnlyBot.fromJSON(convertShaderToPreset(JSON.parse(mintedBot))));
                        redirectToEdition(tokenId.toNumber());
                    }
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        [tokenContract, explode, redirectToEdition]
    );
    const {
        write = (): null => null,
        error: writeError,
        status,
    } = useContractWrite({
        ...config,
        request: gasBufferRequest,
        onSuccess,
    });

    useEffect(() => {
        if (mintedBot) {
            consolidate(materials, scaleAnchor(mintedBot.anchor));
        }
    }, [mintedBot, materials, consolidate]);

    const handleError = useCallback(
        ({ error }: EthersError) => {
            if (error) {
                setMintErrorCode('code' in error ? error.code : 888);

                if (error.code === -32000 && error.message?.includes('insufficient funds')) {
                    setMintErrorMessage(
                        `ERROR: Insufficient funds.\nRequired: ${ethers.utils.formatEther(batchPrice)}E + gas`
                    );
                    setMintErrorAllowRetry(true);
                    return;
                }

                let message = error?.message || 'ERROR: Please try again.';
                if (error?.data?.originalError?.data) {
                    const { interface: iface } = escrowContract;
                    const { name } = iface.parseError(error.data.originalError.data);
                    message = name in errorMapping ? errorMapping[name as keyof typeof errorMapping] : name;
                }
                setMintErrorMessage(message);
            } else {
                setMintErrorCode(888);
                setMintErrorMessage('ERROR: Please try again.');
            }
        },
        [escrowContract, batchPrice]
    );

    const over = (e: ThreeEvent<PointerEvent>): void => {
        e.stopPropagation();
        setHovered(true);
    };
    const out = (): void => {
        setHovered(false);
    };
    useEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);

    useEffect(() => {
        if (writeError !== null) {
            handleError(writeError as EthersError);
        }
    }, [writeError, handleError]);

    useEffect(() => {
        if (prepareError !== null) {
            handleError(prepareError as EthersError);
        }
    }, [prepareError, handleError]);

    useLayoutEffect(() => {
        instancedMeshRefs.forEach((ref, i) => {
            const mesh = ref.current;
            if (!mesh) {
                return;
            }

            const material = materials[i];
            if (!material) {
                mesh.count = 0;
                return;
            }

            mesh.count = material.voxels.length;
            applyMaterial(mesh, material.color, material.preset);

            material.voxels.forEach(({ position: [x, y, z] }, index) => {
                TRANSFORMATION_STORAGE.position.set(x, y, z);
                TRANSFORMATION_STORAGE.scale.setScalar(1);
                TRANSFORMATION_STORAGE.updateMatrix();

                mesh.setMatrixAt(index, TRANSFORMATION_STORAGE.matrix);
                mesh.instanceMatrix.needsUpdate = true;
            });
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useFrame((_, delta) => {
        if (groupRef.current) {
            if (orientation.current && orientation.current.beta !== null && orientation.current.gamma !== null) {
                groupRef.current.rotation.set(
                    (txSubmitted ? 0 : Math.PI / 4) + MathUtils.lerp(0, Math.PI / 8, orientation.current.beta / 180),
                    MathUtils.lerp(txSubmitted ? 0 : Math.PI / 4, Math.PI / 3, orientation.current.gamma / 90),
                    0
                );
            } else {
                groupRef.current.rotation.set(
                    (txSubmitted ? 0 : Math.PI / 4) + MathUtils.lerp(0, Math.PI / 8, mouse.current.y),
                    MathUtils.lerp(txSubmitted ? 0 : Math.PI / 4, Math.PI / 3, mouse.current.x),
                    0
                );
            }
            // lerp position of egg to center
            if (txSubmitted && eggPositionTimer.current < TIME_TO_CENTER_EGG) {
                eggPositionTimer.current += delta;
                groupRef.current.position.set(
                    0,
                    clampedLerp(groupRef.current.position.y, 0, eggPositionTimer.current / TIME_TO_CENTER_EGG),
                    0
                );

                // fade out black background
                backgroundMaterialRef.current.opacity = clampedLerp(
                    1,
                    0,
                    eggPositionTimer.current / TIME_TO_CENTER_EGG
                );

                // fade in light intensity
                light1.current.intensity = clampedLerp(
                    0,
                    LIGHT_INTENSITY,
                    eggPositionTimer.current / TIME_TO_CENTER_EGG
                );
                light2.current.intensity = clampedLerp(
                    0,
                    LIGHT_INTENSITY,
                    eggPositionTimer.current / TIME_TO_CENTER_EGG
                );
            }
        }
        if (mintedBot) {
            timeSinceBotMinted.current += delta;
            backgroundMaterialRef.current.opacity = 0;

            if (timeSinceBotMinted.current < CONSOLIDATE_TIME) {
                // scale bot
                groupRef.current.scale.set(
                    clampedLerp(0.1, BOT_SCALE, timeSinceBotMinted.current / CONSOLIDATE_TIME),
                    clampedLerp(0.1, BOT_SCALE, timeSinceBotMinted.current / CONSOLIDATE_TIME),
                    clampedLerp(0.1, BOT_SCALE, timeSinceBotMinted.current / CONSOLIDATE_TIME)
                );
            } else if (nameRef.current) {
                nameRef.current.fillOpacity = clampedLerp(
                    0,
                    1,
                    (timeSinceBotMinted.current - CONSOLIDATE_TIME) / NAME_FADE_IN_TIME
                );
            }
        }
    });

    const showMintScreen = !txSubmitted || mintErrorCode || mintErrorMessage;

    if (remainingInBatch === 0) {
        return <SoldOut onCancel={cancel} />;
    }

    return (
        <>
            <directionalLight ref={light1} intensity={0} castShadow={false} position={[-1, -1, 0.3]} />
            <directionalLight ref={light2} intensity={0} castShadow={false} position={[-10, 10, 10]} />
            <group>
                <mesh position={[0, 0, -1]}>
                    <planeGeometry attach="geometry" args={[500, 500]} />
                    <meshBasicMaterial ref={backgroundMaterialRef} opacity={1} transparent color={bgBlack} />
                </mesh>
                <Environment
                    background={false}
                    files={'https://anima.supply/cdn/onlybots/generator/onlybots-scene-bw-1k.hdr'}
                />
                <Environment
                    background={'only'}
                    files={'https://anima.supply/cdn/onlybots/generator/onlybots-scene-2k.hdr'}
                />
            </group>

            {mintedBot && (
                <Text
                    ref={nameRef}
                    position={[0, 2, 1]}
                    textAlign="center"
                    font={garamondNarrowLight}
                    fontSize={0.25}
                    letterSpacing={-0.05}
                    color="white"
                    fillOpacity={0}
                >
                    {mintedBot.name.toUpperCase()}
                </Text>
            )}
            <group scale={scale}>
                <group ref={groupRef} scale={EGG_SCALE} position={[0, 1.7, 0]}>
                    {instancedMeshRefs.map((ref, i) => (
                        <SelectBloom key={i} enabled={!mintedBot || (materials[i]?.preset?.bloom ?? false)}>
                            <instancedMesh ref={ref} args={[undefined, undefined, MAX_VOXEL_COUNT]}>
                                <boxGeometry />
                                <meshPhysicalMaterial color={DEFAULT_COLOR} />
                            </instancedMesh>
                        </SelectBloom>
                    ))}
                </group>
            </group>
            {showMintScreen && (
                <Screen>
                    {mintErrorMessage ? (
                        <>
                            <Plane position={[0, 0.4, 2]} material-color="#F00" args={[1.7, 0.39]}>
                                <Text
                                    textAlign="center"
                                    font={helveticaNeue}
                                    fontSize={0.08}
                                    color="white"
                                    anchorX="center"
                                    maxWidth={2.75}
                                    position={[0, -0.01, 0]}
                                >
                                    Mint Failed: Error Code {mintErrorCode?.toString().padStart(3, '0') || 'XXX'}
                                </Text>
                            </Plane>
                            <group position={[0, responsiveValue(0, -0.15), 0]}>
                                <Text anchorY="top" position={[0, responsiveValue(-0.1, 0.18), 0]} {...leadTextProps}>
                                    {mintErrorMessage || 'An error occurred. Please try again.'}
                                </Text>
                            </group>
                        </>
                    ) : status === 'loading' ? (
                        <>
                            <Plane position={[0, 0.4, 2]} material-color="#090" args={[1.7, 0.39]}>
                                <Text
                                    textAlign="center"
                                    font={helveticaNeue}
                                    fontSize={0.08}
                                    color="white"
                                    anchorX="center"
                                    maxWidth={2.75}
                                    position={[0, -0.01, 0]}
                                >
                                    TX Requested
                                </Text>
                            </Plane>
                            <group position={[0, responsiveValue(0, -0.15), 0]}>
                                <Text anchorY="top" position={[0, responsiveValue(-0.1, 0.18), 0]} {...leadTextProps}>
                                    Confirm the transaction in your wallet to proceed.
                                </Text>
                            </group>
                        </>
                    ) : (
                        <group position={[0, responsiveValue(0, -0.22), 0]}>
                            <Text position={[0, responsiveValue(0.11, 0.18), 0]} {...leadTextProps}>
                                Welcome, {walletAddress}!
                            </Text>
                            <group position={[0, responsiveValue(-0.5, -0.43), 0]}>
                                <Text {...leadTextProps}>
                                    Proceed to the incubator to obtain an Onlybot. Its destiny is entrusted to you.
                                </Text>
                            </group>
                        </group>
                    )}
                    <group
                        scale={responsiveValue(1, 0.9)}
                        position={[responsiveValue(-0.9, -0.6), responsiveValue(-1.6, -2.0), 0]}
                    >
                        {status !== 'loading' &&
                            (isPrepareError ? (
                                <>
                                    {mintErrorAllowRetry ? (
                                        <PlaystationButton
                                            clickableAreaDimensions={[1, 0.7]}
                                            maxWidth={4}
                                            position={[0, 0, 0]}
                                            icon="square"
                                            label="RETRY"
                                            action={() => {
                                                void refetch();
                                            }}
                                        />
                                    ) : (
                                        <PlaystationButton
                                            clickableAreaDimensions={[4, 0.7]}
                                            maxWidth={4}
                                            position={[-1.15, 0, 0]}
                                            icon="square"
                                            label="ONLYBOTS MARKETPLACE"
                                            action={() => {
                                                window.location.href = BUY_URL;
                                            }}
                                        />
                                    )}
                                </>
                            ) : (
                                <group position={[responsiveValue(-0.35, -0.45), 0, 0]}>
                                    <mesh position={[2.47, 0.03, 0]} scale={0.26}>
                                        <planeGeometry args={[1, 1]} />
                                        <meshBasicMaterial
                                            ref={xiCharacterRef}
                                            map={xiCharacter}
                                            transparent
                                            opacity={1}
                                            color="white"
                                        />
                                    </mesh>
                                    <PlaystationButton
                                        siblingRef={xiCharacterRef}
                                        clickableAreaDimensions={[4, 0.7]}
                                        icon="square"
                                        label={`CLAIM - ${Number(ethers.utils.formatEther(batchPrice)).toFixed(3)}`}
                                        action={write}
                                    />
                                </group>
                            ))}
                        <PlaystationButton
                            clickableAreaDimensions={[3, 0.7]}
                            position={[0, responsiveValue(-0.8, -0.9), 0]}
                            icon="x"
                            label={status === 'loading' ? 'EXIT' : 'CANCEL'}
                            action={cancel}
                        />
                    </group>
                    <group
                        visible={!isPrepareError}
                        position={responsiveValue([4.1, -1.0, 0], [1.93, -1.6, 0])}
                        rotation={[0, 0, Math.PI / 2]}
                    >
                        <Image position={[-1.7, 0, 0]} url={recaptchaImg1} scale={responsiveValue(0.25, 0.23)} />
                        <group position={[-0.35, 0, 0]}>
                            <Text {...disclaimerTextProps} position={[0, responsiveValue(0.07, 0.07), 0]}>
                                By proceeding to mint, you are agreeing to the
                            </Text>
                            <Text
                                {...disclaimerTextProps}
                                onPointerOver={over}
                                onPointerOut={out}
                                position={[-0.63, -0.06, 0]}
                                onClick={() => window.open('/terms', '_blank')}
                            >
                                Terms and Conditions,
                            </Text>
                            <Text
                                {...disclaimerTextProps}
                                onPointerOver={over}
                                onPointerOut={out}
                                position={[0.32, -0.06, 0]}
                                onClick={() => window.open('/license', '_blank')}
                            >
                                Onlybots License,
                            </Text>
                            <Text position={[0.83, -0.06, 0]} {...disclaimerTextProps}>
                                and
                            </Text>
                            <Text
                                {...disclaimerTextProps}
                                onPointerOver={over}
                                onPointerOut={out}
                                position={[1.33, -0.06, 0]}
                                onClick={() => window.open('/privacy', '_blank')}
                            >
                                Privacy policy.
                            </Text>
                        </group>
                    </group>
                </Screen>
            )}
        </>
    );
};
